"use client"

import React, { useState, useRef, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Copy, ExternalLink, Bitcoin, CreditCard, Phone, Gift, Network, Package } from "lucide-react"
import { getModalZIndex } from "@/lib/utils/z-index-utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import NumericInput from "@/components/ui/numeric-input"
import { PaymentMethod } from "@/types/enums"

interface PaymentsModalProps {
  isOpen: boolean
  onClose: () => void
  mode?: 'payments' | 'other-methods'
}

export function PaymentsModal({ isOpen, onClose, mode = 'payments' }: PaymentsModalProps) {
  const [copiedMethod, setCopiedMethod] = useState<string | null>(null)
  const [showBitcoinAddress, setShowBitcoinAddress] = useState(false)
  const [showSinpeMovil, setShowSinpeMovil] = useState(false)
  const [showComingSoon, setShowComingSoon] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  
  // Other payment methods state
  const [giftAmount, setGiftAmount] = useState<number>(0)
  const [exchangeDescription, setExchangeDescription] = useState<string>('')
  const [exchangeValue, setExchangeValue] = useState<number>(0)
  const [otherMethodName, setOtherMethodName] = useState<string>('')
  const [otherMethodAmount, setOtherMethodAmount] = useState<number>(0)

  const bitcoinAddress = "bc1ql9h7f3mpe4xln7kyutxlsyp09vlacdze0ejxtd"
  const paypalLink = "https://www.paypal.com/donate/?hosted_button_id=7BJAE2K3YAYXG"
  const sinpeMovilPhone = "+506 63212319"

  const copyToClipboard = async (text: string, method: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedMethod(method)
      setTimeout(() => setCopiedMethod(null), 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  const showComingSoonMessage = (service: string) => {
    setShowComingSoon(service)
    setTimeout(() => setShowComingSoon(null), 2000)
  }

  // Center modal on open
  useEffect(() => {
    if (isOpen && modalRef.current && !position) {
      const modal = modalRef.current
      const { width, height } = modal.getBoundingClientRect()
      const x = window.innerWidth / 2 - width / 2
      const y = window.innerHeight / 2 - height / 2
      setPosition({ x, y })
    }
    if (!isOpen) {
      setPosition(null)
      setShowBitcoinAddress(false)
      setShowSinpeMovil(false)
      setShowComingSoon(null)
    }
  }, [isOpen, position])

  // Drag logic (from anywhere in modal)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return // Only left click
    setDragging(true)
    const rect = modalRef.current?.getBoundingClientRect()
    setOffset({
      x: e.clientX - (rect?.left ?? 0),
      y: e.clientY - (rect?.top ?? 0),
    })
  }
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (dragging && modalRef.current) {
      const modal = modalRef.current
      const { width, height } = modal.getBoundingClientRect()
      let x = e.clientX - offset.x
      let y = e.clientY - offset.y
      // Prevent dragging off-screen
      x = Math.max(16, Math.min(x, window.innerWidth - width - 16))
      y = Math.max(16, Math.min(y, window.innerHeight - height - 16))
      setPosition({ x, y })
    }
  }, [dragging, offset])
  const handleMouseUp = () => setDragging(false)

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    } else {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [dragging, offset, handleMouseMove])

  if (!isOpen) return null

  return (
    <div className={`fixed inset-0 ${getModalZIndex()} flex items-center justify-center bg-black/50`}>
      <div
        ref={modalRef}
        className="bg-gray-200 dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4 cursor-move select-none max-h-[90vh] overflow-y-auto"
        style={position ? { position: "fixed", left: position.x, top: position.y, margin: 0 } : {}}
        onMouseDown={handleMouseDown}
      >
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-xl font-bold">How to Pay</CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4 px-4 pb-4">
          {mode === 'payments' && (
            <>
              {/* Bitcoin */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Bitcoin className="h-4 w-4 text-orange-500" />
              <h3 className="font-semibold text-sm">Bitcoin Jungle Wallet</h3>
              <Badge variant="secondary" className="text-xs">Bitcoin / Lightning Network</Badge>
            </div>
            
            {!showBitcoinAddress ? (
              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-8 text-sm"
                onClick={() => setShowBitcoinAddress(true)}
              >
                <Bitcoin className="h-3 w-3 mr-1" />
                Bitcoin
              </Button>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                <p className="text-xs text-muted-foreground mb-1">Bitcoin Address:</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white dark:bg-gray-700 px-2 py-1 rounded flex-1 break-all">
                    {bitcoinAddress}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(bitcoinAddress, 'bitcoin')}
                    className="shrink-0 h-6 px-2"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                {copiedMethod === 'bitcoin' && (
                  <p className="text-xs text-green-600 mt-1">✓ Copied!</p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBitcoinAddress(false)}
                  className="w-full mt-2 h-6 text-xs"
                >
                  Hide Address
                </Button>
              </div>
            )}
          </div>

          {/* PayPal */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-blue-500" />
              <h3 className="font-semibold text-sm">PayPal</h3>
              <Badge variant="secondary" className="text-xs">Card / Balance</Badge>
            </div>
            
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-8 text-sm"
              onClick={() => window.open(paypalLink, '_blank')}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              PayPal
            </Button>
          </div>

          {/* Sinpe Móvil */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-sm">Sinpe Móvil</h3>
              <Badge variant="secondary" className="text-xs">Phone Number / CR Mobile Payment</Badge>
            </div>
            
            {!showSinpeMovil ? (
              <Button 
                className="w-full bg-green-700 hover:bg-green-700 text-white h-8 text-sm"
                onClick={() => setShowSinpeMovil(true)}
              >
                <Phone className="h-3 w-3 mr-1" />
                Sinpe Móvil
              </Button>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                <p className="text-xs text-muted-foreground mb-1">Phone Number:</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white dark:bg-gray-700 px-2 py-1 rounded flex-1 break-all">
                    +506 63212319
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard("63212319", 'sinpe')}
                    className="shrink-0 h-6 px-2"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                {copiedMethod === 'sinpe' && (
                  <p className="text-xs text-green-600 mt-1">✓ Copied!</p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSinpeMovil(false)}
                  className="w-full mt-2 h-6 text-xs"
                >
                  Hide Number
                </Button>
              </div>
            )}
          </div>
            </>
          )}

          {mode === 'other-methods' && (
            <>
              {/* Other Payment Methods */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-sm mb-3">Other Payment Methods</h3>
                
                {/* Gift Method */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-pink-500" />
                    <h4 className="font-semibold text-sm">Gift</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Amount:</Label>
                    <NumericInput
                      value={giftAmount}
                      onChange={setGiftAmount}
                      placeholder="0.00"
                      className="w-24 h-6 text-xs"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // TODO: Auto-fill from revenue
                        setGiftAmount(0);
                      }}
                      className="h-6 px-2 text-xs"
                    >
                      Auto
                    </Button>
                  </div>
                </div>

                {/* Exchange Method */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Network className="h-4 w-4 text-blue-500" />
                    <h4 className="font-semibold text-sm">Exchange</h4>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Exchanged for:</Label>
                      <Input
                        value={exchangeDescription}
                        onChange={(e) => setExchangeDescription(e.target.value)}
                        placeholder="e.g., Rent, Services, Materials"
                        className="h-6 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Value:</Label>
                      <NumericInput
                        value={exchangeValue}
                        onChange={setExchangeValue}
                        placeholder="0.00"
                        className="w-24 h-6 text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Other Method */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-gray-500" />
                    <h4 className="font-semibold text-sm">Other</h4>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Method name:</Label>
                      <Input
                        value={otherMethodName}
                        onChange={(e) => setOtherMethodName(e.target.value)}
                        placeholder="e.g., Barter, Credit, Installment"
                        className="h-6 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Amount:</Label>
                      <NumericInput
                        value={otherMethodAmount}
                        onChange={setOtherMethodAmount}
                        placeholder="0.00"
                        className="w-24 h-6 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

            {/* Lightning Network 
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-sm flex items-center justify-center">
                <div className="h-2 w-2 bg-white rounded-sm"></div>
              </div>
              <h3 className="font-semibold text-sm">Lightning Network</h3>
              <Badge variant="secondary" className="text-xs">Bitcoin</Badge>
            </div>
            
             Coming Soon Notification 
            {showComingSoon && (
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-center animate-in fade-in duration-200">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  ✨ {showComingSoon} integration coming soon! We're working on making this payment method available.
                </p>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white h-8 text-sm"
                onClick={() => showComingSoonMessage('Zaps')}
              >
                <div className="h-3 w-3 mr-1 bg-white rounded-sm"></div>
                Zaps
              </Button>
              
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700 text-white h-8 text-sm"
                onClick={() => showComingSoonMessage('Strike')}
              >
                <div className="h-3 w-3 mr-1 bg-white rounded-sm"></div>
                Strike
              </Button>
            </div>
          </div> */}

          {/* Project Info 
          <div className="bg-blue-50 dark:bg-blue-950/20 p-2 pb-1 rounded">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1 text-sm">
              What your donation supports:
            </h4>
            <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-0.5">
              <li>• Materials for ramp construction</li>
              <li>• Professional skateboarding equipment</li>
              <li>• Community outreach programs</li>
              <li>• Youth development initiatives</li>
            </ul>
            <p className="text-xs text-blue-800 dark:text-blue-200 mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
              You can also donate materials, equipment, or services that are required for the project. We will assign a monetary value to these contributions based on the budget.
            </p>
          </div>*/}

          {/* Disclaimer */}
          <div className="bg-amber-50 dark:bg-amber-950/20 p-2 rounded border border-amber-200 dark:border-amber-950">
            <h4 className=" text-amber-700 dark:text-amber-400  text-sm">
              Notice:
            </h4>
            <br />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Please make sure you contact me previously if you are buying an item, 
              since we have different processes for different locations and there could be shipping extra costs and infomation we required to send the item. <br /> <br />✨ Thanks!
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Paypal payments will have a 5% fee that Paypal charges.
            </p>

          </div>

          {/* Close Button */}
          <Button 
            variant="outline" 
            onClick={onClose}
            className="w-full h-8 text-sm"
          >
            Close
          </Button>
        </CardContent>
      </div>
    </div>
  )
} 